import React, { useMemo } from 'react';
import { FeatureAppLoader } from '@feature-hub/react';
import styled from 'styled-components';
import { SYS_BREAKPOINT_MD } from '@oneaudi/unified-web-common';
import { getVideoPlayerConfig } from '../../utils/getVideoPlayerConfig';
import { useWindowSize } from '../../hooks/useWindowSize';

// Video Player Feature App configuration
const VIDEO_PLAYER_FA_NAME = 'fa-video-player';
const VIDEO_PLAYER_FA_VERSION = 'v5.5.0';
const VIDEO_PLAYER_FA_ENV = 'prod';
const VIDEO_PLAYER_FA_BASE_URL = `https://${VIDEO_PLAYER_FA_NAME}.cdn.${VIDEO_PLAYER_FA_ENV}.collab.apps.one.audi/${VIDEO_PLAYER_FA_VERSION}`;

interface VideoPlayerProps {
  height: number;
  // Video asset properties from existing implementation
  srcs?: {
    xl?: { src?: string; poster?: string };
    s?: { src?: string; poster?: string };
  };
  alt?: string;
  alternateDescriptionPortrait?: string;
  autoPlay?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  closedCaption?: { path?: string };
  closedCaptionPortrait?: { path?: string };
  audioDescription?: { path?: string };
  audioDescriptionPortrait?: { path?: string };
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  height,
  srcs,
  alt,
  alternateDescriptionPortrait: altPortrait,
  autoPlay,
  autoplay,
  loop,
  closedCaption: caption,
  closedCaptionPortrait: captionPortrait,
  audioDescription,
  audioDescriptionPortrait,
}) => {
  const { width } = useWindowSize();
  const isMobile = width < parseInt(SYS_BREAKPOINT_MD.replace('px', ''), 10);

  const shouldAutoPlay = Boolean(autoPlay ?? autoplay);

  const videoPlayerConfig = useMemo(() => {
    return getVideoPlayerConfig({
      alt: alt || '',
      altPortrait: altPortrait || alt || '',
      audioDescription: audioDescription?.path,
      audioDescriptionPortrait: audioDescriptionPortrait?.path,
      autoPlay: shouldAutoPlay,
      caption: caption?.path,
      captionPortrait: captionPortrait?.path,
      loop: loop || false,
      muted: shouldAutoPlay, // Mute when autoplay is enabled for better UX
      videoUrlLargeScreen: srcs?.xl?.src || '',
      videoUrlLargeScreenPoster: srcs?.xl?.poster || '',
      videoUrlSmallScreen: srcs?.s?.src || '',
      videoUrlSmallScreenPoster: srcs?.s?.poster || '',
      isMobile,
    });
  }, [
    alt,
    altPortrait,
    audioDescription?.path,
    audioDescriptionPortrait?.path,
    shouldAutoPlay,
    caption?.path,
    captionPortrait?.path,
    loop,
    srcs?.xl?.src,
    srcs?.xl?.poster,
    srcs?.s?.src,
    srcs?.s?.poster,
    isMobile,
  ]);

  // Dynamic URL generation based on legacy/alpha mode
  const serverSrcFile = process.env.IS_LEGACY ? '/fh/app.node.js' : '/fh/app-alpha.node.js';
  const clientSrcFile = process.env.IS_LEGACY ? '/fh/app.js' : '/fh/app-alpha.js';

  return (
    <VideoPlayerWrapper height={height}>
      <FeatureAppLoader
        config={videoPlayerConfig}
        featureAppId="your-feature-app-id_fa-video-player"
        serverSrc={VIDEO_PLAYER_FA_BASE_URL + serverSrcFile}
        src={VIDEO_PLAYER_FA_BASE_URL + clientSrcFile}
      />
    </VideoPlayerWrapper>
  );
};

const VideoPlayerWrapper = styled.div<{ height: number }>`
  position: unset;
  height: ${({ height }) => height}px;
  padding: 0;
  div {
    height: auto;
    padding-bottom: 0;
    video {
      height: ${({ height }) => height}px;
      position: unset;
    }
  }
`;
